import { useEffect, useMemo } from 'react'

import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { fetchOfferList } from 'actions/OfferActions'
import arrayShuffle from 'lib/arrayShuffle'
import memoize from 'lib/memoize'
import getOfferListKey from 'lib/offerListKey'
import { without } from 'lib/arrayUtils'
import { getSoldOutOfferIds } from 'selectors/offerSelectors'

export interface Options {
  /** For when the offer list is no longer needed, but still needs to be called to avoid violating the Rules of Hooks */
  disabled?: boolean;
  randomize?: boolean;
  excludedIds?: string[];
}

/**
 * Memoize is used here as it provides a stable set of offer ids for that exact version of the array
 * This ensures that we always return the same randomised set throughout the life of the page
 */
const getRandomizedOfferIds = memoize((offerIds: Array<string>) => arrayShuffle(offerIds))

const emptyOfferList: App.OfferList = {
  offerIds: [],
  fetching: true,
  error: null,
}

const emptyDisabledOfferList: App.OfferList = {
  offerIds: [],
  fetching: false,
  error: null,
}

/**
 * Features to add support for
 * - Property search
 */

function useOfferList(filters: App.OfferListFilters, options: Options = {}): [App.OfferList, string] {
  const {
    randomize,
    disabled,
    excludedIds,
  } = options

  const dispatch = useAppDispatch()

  const listKey = useMemo(() => {
    return getOfferListKey(filters)
  }, [filters])

  useEffect(() => {
    if (disabled) {
      // Do nothing
    } else {
      dispatch(fetchOfferList(filters))
    }
    // eslint-disable-next-line
  }, [listKey])

  const offerList = useAppSelector((state): App.OfferList => {
    if (disabled) {
      return emptyDisabledOfferList
    }
    return state.offer.offerLists[listKey] ?? emptyOfferList
  })

  const finalOfferList = useMemo(() => {
    const postExclusionIds = excludedIds ? without(offerList.offerIds, ...excludedIds) : offerList.offerIds

    if (randomize) {
      return {
        ...offerList,
        offerIds: getRandomizedOfferIds(postExclusionIds),
      }
    }
    return {
      ...offerList,
      offerIds: postExclusionIds,
    }
  }, [randomize, offerList, excludedIds])

  return [finalOfferList, listKey]
}

// filter out sold out offers
function useFilterSoldOutOffers(offerList: App.OfferList): App.OfferList {
  const soldOutOfferIds = useAppSelector(getSoldOutOfferIds)

  return useMemo(() => {
    const notSoldOutIds = offerList.offerIds.filter(id => !soldOutOfferIds.has(id))
    return {
      offerIds: notSoldOutIds,
      error: offerList.error,
      fetching: offerList.fetching,
    }
  }, [offerList, soldOutOfferIds])
}

export { useOfferList, useFilterSoldOutOffers }
